import { useEffect } from 'react';

/**
 * Shared global popup keyboard handler (§3 "Shared modal convention" / parity
 * 126), mirroring the Backbone MainView body keydown handler that runs ONLY
 * while a `.popup-wrapper.active` is open:
 *  - Esc(27) → clicks the active popup's Cancel button + dismisses the popup.
 *  - Enter(13) → clicks the active popup's `button[autofocus]`.
 *
 * The handler operates on the LAST (topmost) `.popup-wrapper.active` in the DOM,
 * unless `blanket` is set (see `PopupKeysOptions`).
 *
 * Manage-Columns (`#whiteboard_sett`): Esc DOES close it. settings.js's own
 * `keyup body` Esc handler is commented out (§3.G), but the GLOBAL MainView body
 * keyup handler (main.js) fires for ANY `.popup-wrapper.active` and, on Esc,
 * clicks `[name=cancel]` + removes the popup. So there is no per-popup Esc
 * exception — Esc cancels every active popup, including Manage Columns.
 *
 * Implementation note: we click the popup's Cancel/autofocus button rather than
 * imperatively removing DOM, so React's own onClick handlers (which flip the
 * dashboardSlice modal flags + unmount the popup) run. This matches the
 * Backbone "click the button" semantics and keeps state authoritative.
 */
export interface PopupKeysOptions {
  /**
   * ★ Phase 4 (`/sizer/`) — the LEGACY BLANKET SEMANTICS of
   * `django/sizer/static/sizer/mvc/view/main.js:150-160`, which the sizer route
   * opts into. Three differences from the default:
   *
   *  1. **Every** active `.popup-wrapper` is dismissed, not just the topmost —
   *     legacy does `$('.popup-wrapper.active').removeClass('active')` on the
   *     whole jQuery set, and the sizer can genuinely have two open at once
   *     (open Settings, then click "more notes…": `#moreNotes` sits outside any
   *     `.popup-wrapper`, so the backdrop branch never fires and both stay
   *     active).
   *  2. The header Ratings popup is **included** rather than exempted — see
   *     `dismissActiveSizerPopups` for the decision and its rationale.
   *  3. The sizer's own `close_settings` / `close_notes` buttons count as the
   *     cancel affordance. Scoped to this mode on purpose:
   *     `button[name="close_notes"]` also exists on the AHU board
   *     (`pages/ahu-board/forms/selector/NotesPopup.tsx:33`), and whether Esc
   *     should close THAT popup is a different page's parity question.
   */
  blanket?: boolean;
}

const CANCEL_SELECTOR =
  'button[name="cancel"], button[name="cancel_sort"], button[name="cancel_delete"], button[name="cancel_btn"], button[name="cancel_change"], button#cancel, button[name="cancel_delete_section_btn"], button[name="cancel_action"]';

/** `blanket` additions — the sizer's Settings / more-notes CLOSE buttons and the
 * header Ratings popup's `#close[name="close_btn"]`. */
const BLANKET_CANCEL_SELECTOR = `${CANCEL_SELECTOR}, button[name="close_settings"], button[name="close_notes"], button[name="close_btn"]`;

/** Click each popup's cancel/close affordance so React state (and, for the
 * Ratings popup, `localStorage`) stays authoritative. Returns whether anything
 * was actually dismissed. */
function dismissPopups(popups: HTMLElement[], cancelSelector: string): boolean {
  let acted = false;
  for (const popup of popups) {
    // An earlier iteration may already have unmounted this node — the sizer's
    // `close_notes` removes EVERY `.notes` at once (main.js:700-702).
    if (!popup.isConnected) continue;
    // Fallback = `#ok` ONLY — reliance-grepped (QA batch 7 FIX 7): `id="ok"`
    // exists solely on the dashboard NoPermissionDialog, an ACK dialog whose OK
    // is its close affordance. NEVER re-add `button[autofocus]` here: autofocus
    // marks the ENTER primary (Insert / Done / Duplicate / lug Confirm), and
    // legacy Esc never clicks a primary (main.js:334-356 clicks
    // `button[name="cancel"]`, then closes the wrapper regardless).
    const cancel =
      popup.querySelector<HTMLButtonElement>(cancelSelector) ??
      popup.querySelector<HTMLButtonElement>('#ok');
    if (cancel) {
      cancel.click();
      acted = true;
    }
  }
  return acted;
}

/**
 * The dismissal half of the sizer's blanket body handlers, exported so the
 * backdrop-click branch (`main.js:161-167`) and the Esc branch
 * (`main.js:152-154`) share ONE implementation — they share one in legacy too
 * (both end at `$('.popup-wrapper.active').removeClass('active')`).
 *
 * ★★ ORCHESTRATOR DECISION — the header "Independent Rating" popup.
 * On the LEGACY sizer these handlers hit `.component-selector-popup` as well:
 * Esc strips its `.active` but leaves `localStorage.independent_rating_popup_state`
 * at `"open"` (VERIFIED live — discovery §8b), so the header silently re-opens
 * it on the next page load; it even broke one of the discovery agent's probes.
 * The React sizer reproduces the VISIBLE behaviour (the popup closes) but writes
 * the state CORRECTLY, by clicking the popup's own `#close[name="close_btn"]`
 * button, whose handler calls `setPopupState('close')`
 * (`pages/dashboard/components/header/useIndependentRating.ts:105-111`, using
 * the Phase 6.5 `independentRatingStorage.ts` helpers).
 *
 * Rationale for NOT reproducing the desync: `independent_rating_popup_state` is
 * SHARED with the still-live legacy popup on `/admin/` and `/partners/` (see the
 * byte-compatibility note atop `independentRatingStorage.ts`), so deliberately
 * persisting a state we know to be wrong could misbehave on pages this migration
 * has not touched.
 *
 * ★ 4e — `excludeSelector`. The BACKDROP branch passes `.sizerSelection`,
 * because the selection popup is the one active popup whose Esc and backdrop
 * behaviours DIFFER. Esc genuinely closes it: it binds its own
 * `$(document).keyup` (`header/view/sizer_popup.js:143-146`) → `close_popup()`,
 * which clears `sizer_popup`, `d-sizer-airflow` and the field selections. A
 * backdrop click only strips `.active` — `main.js:161-167` is the only handler
 * that sees it, and the popup's state machine stays at `opened`, so the next
 * cell click brings it back intact. Clicking its `button[name="cancel"]` for a
 * backdrop click would turn that reversible hide into a full teardown, so
 * `useSizerBackdropClick` excludes it here and hands it to the controller's
 * `dismissByBackdrop()` instead. The Esc branch passes nothing and keeps hitting
 * it, which is the correct legacy outcome.
 */
export function dismissActiveSizerPopups(excludeSelector?: string): boolean {
  let popups = Array.from(document.querySelectorAll<HTMLElement>('.popup-wrapper.active'));
  if (excludeSelector) popups = popups.filter((el) => !el.matches(excludeSelector));
  if (popups.length === 0) return false;
  return dismissPopups(popups, BLANKET_CANCEL_SELECTOR);
}

export function usePopupKeys(options: PopupKeysOptions = {}): void {
  const blanket = !!options.blanket;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Enter') return;
      // contentEditable guard (project-board MainView body.keyup): inside a
      // contenteditable, Enter inserts a newline and Esc is ignored — the only
      // effective part of the Backbone INPUT/SELECT/TEXTAREA guard (the rest
      // compares e.target.tag, which is always undefined).
      const editTarget = e.target as HTMLElement | null;
      if (editTarget?.isContentEditable) return;
      const all = Array.from(document.querySelectorAll<HTMLElement>('.popup-wrapper.active'));
      // ★ Phase 6.5 EXPLICIT EXEMPTION — the header "Independent Rating" popup
      // (`.component-selector-popup`) is NOT Esc/Enter-closable on the boards.
      //
      // Legacy parity: on `/partners/` (and every non-sizer page) Esc does
      // NOTHING to this popup — there is no handler. It DOES close on `/sizer/`,
      // via that page's own blanket handler
      // (sizer/static/sizer/mvc/view/main.js:150-160, `$('body').keyup` → any
      // `.popup-wrapper.active` → removeClass('active')), which also BYPASSES the
      // state setter and leaves the stored state stale.
      //
      // ★ UPDATED IN PHASE 4 — this comment used to justify the exemption with
      // "no React page is the sizer". That premise is DEAD: `/sizer/` is a React
      // route now (`App.tsx` → `pages/sizer/SizerPage.tsx`). It opts in with
      // `usePopupKeys({ blanket: true })`, which drops the filter and dismisses
      // the Ratings popup through its own close button so the persisted state is
      // written correctly (see `dismissActiveSizerPopups`). Every OTHER page
      // keeps the exemption exactly as before.
      //
      // On the boards the exemption is also ACCIDENTAL: the popup's close button
      // is `#close[name="close_btn"].close` (component_selector.popup.tmpl:5),
      // which matches none of the default cancel selectors, and the popup has no
      // `#ok` and no `button[autofocus]`. Making it explicit means a future edit
      // to that markup cannot silently turn Esc-close on.
      //
      // NOTE it is FILTERED OUT rather than short-circuiting the handler: the
      // rating popup is portalled to <body> and is frequently the topmost active
      // `.popup-wrapper`, so bailing would make Esc stop working for whatever
      // real popup is underneath it.
      const popups = blanket
        ? all
        : all.filter((el) => !el.classList.contains('component-selector-popup'));
      if (popups.length === 0) return;
      // Act on the topmost (last-rendered) active popup — or, in blanket mode,
      // on all of them.
      const targets = blanket ? popups : [popups[popups.length - 1]];

      if (e.key === 'Enter') {
        // Avoid double-submitting when focus is in a textarea (newline) — there
        // are no textareas in these popups, but guard defensively.
        const target = e.target as HTMLElement | null;
        if (target && target.tagName === 'TEXTAREA') return;
        // ⚠ Legacy's Enter branch looks for `button[autofocus="true"]` and finds
        // nothing in any sizer popup, so Enter is inert there. React matches
        // `button[autofocus]` (what `autoFocus` in JSX emits) — do NOT add
        // `autoFocus` to a ported sizer popup or you invent behaviour that never
        // existed (discovery §8b).
        for (const popup of targets) {
          const autofocus = popup.querySelector<HTMLButtonElement>('button[autofocus]');
          if (autofocus) {
            e.preventDefault();
            autofocus.click();
          }
        }
        return;
      }

      // Escape: click the popup's Cancel button (covers cancel / cancel_sort /
      // cancel_delete / cancel_change / cancel_delete_section_btn /
      // cancel_action naming + the Workflow popup's #cancel —
      // its Backbone view binds its own keydown-ESC → remove, and the MainView
      // fallback removes any active popup-wrapper anyway); for the NoPermission
      // popup (only an OK button) there is no Cancel — fall through to
      // dismissing via the OK button so Esc still closes it (matches "removes
      // the active popup").
      if (dismissPopups(targets, blanket ? BLANKET_CANCEL_SELECTOR : CANCEL_SELECTOR)) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [blanket]);
}
